/* eslint-disable max-lines-per-function */
import {
  ECompanyConfigEnabledFlagListType,
  EFeatureListType,
  ELoginConfigType,
  EMainProductCodeType,
  EProductSubscriptionType,
  ESubProductCodeType,
  ICompany
} from '@webtrader/core/api/config/company';

export const validateCompanyAccess = (company?: ICompany) => {
  // Root Admin to control hide and show for Backoffice and Membersite -- Start
  let hasMarginAccess = false;
  let hasFundManagementAccess = false;

  let hasThirdPartyAccess = false;
  let hasNewsAccess = false;
  let hasCalendarAccess = false;
  let hasCopyTradeAccess = false;
  let hasDealerAccess = false;
  let hasSignalAccess = false;
  let hasEducationAccess = false;
  let hasTradingViewChartAccess = false;

  let hasAccountClosureAccess = false;
  let hasResetPasswordAccess = false; // CRM account do reset password
  let hasChangePasswordAccess = false;
  let hasForgotPasswordAccess = false; // MT4/MT5 account do forgot password
  let hasCustomerServiceAccess = false;

  let hasTopGainerAccess = false;
  let hasTopLoserAccess = false;
  let hasTopPickAccess = false;
  let hasExperienceAccess = false;

  let hasCompanyLoginConfig = false;
  let hasMT4LiveLoginConfig = false;
  let hasMT4DemoLoginConfig = false;
  let hasMT5LiveLoginConfig = false;
  let hasMT5DemoLoginConfig = false;
  // Root Admin to control hide and show for Backoffice and Membersite -- End

  // Backoffice to control hide and show for Membersite -- Start
  let hasDemoAccountCreation = false;
  let hasOneClickTrading = false;
  let hasEducationalVideo = false;
  let hasSignal = false;
  let hasPremiumSignalAccess = false; // Acuity Signal Subscription
  let hasFreemiumSignalAccess = false;
  let hasPretradeDetails = false;
  let hasEstimatedMarginRequired = false;
  let hasTradingViewChart = false;
  // Backoffice to control hide and show for Membersite -- End

  if (company) {
    const {
      fundManagement,
      mainProductCodeList = [],
      subProductCodeList = [],
      featureList = [],
      loginConfigList = [],
      companyConfigEnabledFlagList = [],
      companySubscriptionList = []
    } = company;
    hasFundManagementAccess = fundManagement;

    hasMarginAccess = mainProductCodeList.some(
      (code) => code === EMainProductCodeType.METATRADER4 || code === EMainProductCodeType.METATRADER5
    );

    hasNewsAccess = subProductCodeList.some((code) => code === ESubProductCodeType.NEWS);
    hasSignalAccess = subProductCodeList.some((code) => code === ESubProductCodeType.SIGNAL);
    hasCalendarAccess = subProductCodeList.some((code) => code === ESubProductCodeType.CALENDAR);
    hasCopyTradeAccess = subProductCodeList.some((code) => code === ESubProductCodeType.COPY_TRADE);
    hasDealerAccess = subProductCodeList.some((code) => code === ESubProductCodeType.DEALER);
    hasEducationAccess = subProductCodeList.some((code) => code === ESubProductCodeType.EDUCATION);
    hasTradingViewChartAccess = subProductCodeList.some((code) => code === ESubProductCodeType.TRADING_VIEW);

    hasAccountClosureAccess = featureList.some((code) => code === EFeatureListType.ACCOUNT_CLOSURE);
    hasResetPasswordAccess = featureList.some((code) => code === EFeatureListType.RESET_PASSWORD);
    hasChangePasswordAccess = featureList.some((code) => code === EFeatureListType.CHANGE_PASSWORD);
    hasCustomerServiceAccess = featureList.some((code) => code === EFeatureListType.CUSTOMER_SERVICE);
    hasTopGainerAccess = featureList.some((code) => code === EFeatureListType.TOP_GAINER);
    hasTopLoserAccess = featureList.some((code) => code === EFeatureListType.TOP_LOSER);
    hasTopPickAccess = featureList.some((code) => code === EFeatureListType.TOP_PICK);
    hasExperienceAccess = featureList.some((code) => code === EFeatureListType.EXPERIENCE);
    hasForgotPasswordAccess = featureList.some((code) => code === EFeatureListType.FORGOT_PASSWORD);

    hasCompanyLoginConfig = loginConfigList.some((config) => config === ELoginConfigType.COMPANY);
    hasMT4LiveLoginConfig = loginConfigList.some((config) => config === ELoginConfigType.METATRADER4_LIVE);
    hasMT4DemoLoginConfig = loginConfigList.some((config) => config === ELoginConfigType.METATRADER4_DEMO);
    hasMT5LiveLoginConfig = loginConfigList.some((config) => config === ELoginConfigType.METATRADER5_LIVE);
    hasMT5DemoLoginConfig = loginConfigList.some((config) => config === ELoginConfigType.METATRADER5_DEMO);

    hasPremiumSignalAccess =
      companySubscriptionList?.filter((item) => item.productCode === ESubProductCodeType.SIGNAL)[0]
        ?.productSubscription === EProductSubscriptionType.PREMIUM;

    hasFreemiumSignalAccess = hasSignalAccess && !hasPremiumSignalAccess;

    hasThirdPartyAccess = hasNewsAccess || hasSignalAccess || hasCalendarAccess;

    hasDemoAccountCreation = companyConfigEnabledFlagList?.some(
      (config) => config === ECompanyConfigEnabledFlagListType.DEMO_ACCOUNT_CREATION
    );
    hasOneClickTrading = companyConfigEnabledFlagList?.some(
      (config) => config === ECompanyConfigEnabledFlagListType.ONE_CLICK_TRADING
    );
    hasEducationalVideo = hasEducationAccess
      ? companyConfigEnabledFlagList?.some((config) => config === ECompanyConfigEnabledFlagListType.EDUCATIONAL_VIDEO)
      : false;
    hasSignal = hasSignalAccess
      ? companyConfigEnabledFlagList?.some((config) => config === ECompanyConfigEnabledFlagListType.SIGNAL)
      : false;
    hasPretradeDetails = companyConfigEnabledFlagList?.some(
      (config) => config === ECompanyConfigEnabledFlagListType.PRETRADE_DETAILS
    );
    hasEstimatedMarginRequired = companyConfigEnabledFlagList?.some(
      (config) => config === ECompanyConfigEnabledFlagListType.ESTIMATED_MARGIN_REQUIRED
    );
    hasTradingViewChart = companyConfigEnabledFlagList?.some(
      (config) => config === ECompanyConfigEnabledFlagListType.TRADING_VIEW
    );
  }

  return {
    hasNewsAccess,
    hasMarginAccess,
    hasSignalAccess,
    hasCalendarAccess,
    hasCopyTradeAccess,
    hasDealerAccess,
    hasEducationAccess,
    hasTradingViewChartAccess,
    hasAccountClosureAccess,
    hasResetPasswordAccess,
    hasChangePasswordAccess,
    hasCustomerServiceAccess,
    hasTopGainerAccess,
    hasTopLoserAccess,
    hasTopPickAccess,
    hasExperienceAccess,
    hasFundManagementAccess,
    hasForgotPasswordAccess,
    hasCompanyLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT4DemoLoginConfig,
    hasMT5LiveLoginConfig,
    hasMT5DemoLoginConfig,
    hasPremiumSignalAccess,
    hasFreemiumSignalAccess,
    hasThirdPartyAccess,
    hasDemoAccountCreation,
    hasOneClickTrading,
    hasEducationalVideo,
    hasSignal,
    hasPretradeDetails,
    hasEstimatedMarginRequired,
    hasTradingViewChart
  };
};
